/**
 * vocab.ts — the SB-1 slug vocabularies (0107 + module validation), UI side.
 * The server owns the slugs; the UI owns the display labels (the 0097
 * stage-slug posture). Legacy free-text values (pre-SB-1 rows, incl. one
 * real-client row) are rendered VERBATIM by the label helpers — data
 * honesty: never re-label a value the vocabulary doesn't know.
 */

export const SOURCE_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'call', label: 'Call' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'conversation', label: 'Conversation' },
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
] as const

export const LOST_REASON_OPTIONS = [
  { value: 'price', label: 'Price' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'timing', label: 'Timing' },
  { value: 'no_budget', label: 'No budget' },
  { value: 'no_decision', label: 'No decision' },
  { value: 'went_quiet', label: 'Went quiet' },
  { value: 'other', label: 'Other' },
] as const

export function sourceLabel(value: string): string {
  return SOURCE_OPTIONS.find((o) => o.value === value)?.label ?? value
}

export function lostReasonLabel(value: string): string {
  return LOST_REASON_OPTIONS.find((o) => o.value === value)?.label ?? value
}

/**
 * The Lost-close confirm gate, mirroring the server rules exactly (SB-1):
 * a reason slug is UI-required (as the free-text reason always was), and
 * the note is required precisely when the slug is 'other' — a bare "other"
 * records nothing. Whitespace is not a note.
 */
export function lostCloseReady(reason: string, note: string): boolean {
  if (reason === '') return false
  if (reason === 'other' && note.trim() === '') return false
  return true
}

/** The 0097 priority CHECK vocabulary (low / medium / high). Shared by the
 *  New Deal drawer's local list and the E2 deal editor; a stored value outside
 *  it is rendered verbatim, never blanked (the stageLabel rule). */
export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
] as const

/**
 * The 2.3a forecast-category vocabulary (0123's CHECK, which is 0120's
 * snapshot CHECK verbatim). Four slugs — and NOT a fifth: "Uncategorised" is
 * what NULL renders as, never a stored value (P2-9 — nobody has said, and the
 * stage is never asked). Picking it CLEARS the field (clearForecastCategory),
 * which is why it lives in the picker list below but not in this array.
 *
 * Labels are F-2's exactly. The server owns the slugs; the UI owns the labels,
 * the same posture as SOURCE_OPTIONS above.
 */
export const FORECAST_CATEGORY_OPTIONS = [
  { value: 'commit', label: 'Commit' },
  { value: 'best_case', label: 'Best case' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'omitted', label: 'Omitted' },
] as const

/** What NULL reads as. A real state a rep can see and pick, never an empty
 *  cell: "nobody has categorised this deal" is information, and rendering it
 *  as blank would say "this field does not exist" instead (F-2 / decision 3). */
export const FORECAST_UNCATEGORISED_LABEL = 'Uncategorised'

/** The picker's five rows: Uncategorised FIRST (it is the state every deal is
 *  born in), then the four slugs in forecast order. The empty value is the
 *  clear, matching the draft's '' — see dealEdit.ts. */
export const FORECAST_CATEGORY_PICKER = [
  { value: '', label: FORECAST_UNCATEGORISED_LABEL },
  ...FORECAST_CATEGORY_OPTIONS,
] as const

/**
 * Slug → label, with NULL/'' → "Uncategorised". A value outside the
 * vocabulary renders VERBATIM (the sourceLabel rule): 0123's CHECK makes one
 * impossible today, but a future slug must never render as blank or as a
 * fabricated "Uncategorised".
 */
export function forecastCategoryLabel(value: string | null | undefined): string {
  if (value == null || value === '') return FORECAST_UNCATEGORISED_LABEL
  return FORECAST_CATEGORY_OPTIONS.find((o) => o.value === value)?.label ?? value
}
